// One page per person: Account, Rider, Driver.
//
// Replaces the two-column wall at /users/:id and folds the driver record into it. What
// this fixes is a ring nobody planned — the user page linked to the old approvals
// funnel, the driver profile linked back to the funnel AND to the user page, and none
// of the three held the whole person. See pages/UserPagePrototype/NOTES.md for the
// variants this came out of.
//
// Three tabs, not two: the old page already split on `{user.rider_id && …}` versus
// `{user.driver_id && …}`, so Account / Rider / Driver falls straight out. "User +
// Driver" would leave the rider cards in a tab called User, empty for a driver-only
// account.
//
// There is deliberately NO Log tab. The prototype had one, on the argument that a
// standing change and a support credit are both "what happened to this person" — but
// the only event log that exists is driver-derived (`eventLog` reads the document
// record and the funnel history). A tab promising cross-domain history would be a
// promise the data cannot keep, so the driver log stays inside the Driver tab where
// its data comes from.
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { GrowthBook } from '@growthbook/growthbook-react';
import { Box, Paper, Stack, Typography, LinearProgress, IconButton, Menu, MenuItem } from '../../ui/core';
import { flushPageSx, hairline, scrollY, surfaceCardSx } from '../../ui/styles';
import DashboardLayout from '../../layouts/DashoardLayout';
import { ChevronLeftIcon, MoreVertIcon } from '../../ui/icons';
import { SegmentedControl } from '../DriverProfile/components';
import { useHasPermission } from '../../helpers/api/auth/index';
import { PERMISSIONS } from '../../helpers/api/auth/permissions';
import useQuery from '../../hooks/useQuery';
import useToast from '../../hooks/useToast';
import { patchSearch } from '../../helpers/utils';
import { fetchUserById, deleteUser } from '../../redux/Users/actions';
import ConfirmModal from '../../components/shared/ConfirmModal';
import ProfileImageUploader from '../../components/widgets/Users/ProfileImageUploader';
import config from '../../config';

import ServiceStrip from './ServiceStrip';
import AccountTab from './AccountTab';
import RiderTab from './RiderTab';
import DriverTab from './DriverTab';

/**
 * Back to wherever they came from, falling back to the user list.
 *
 * `goBack` rather than a link to /users, because the list's search and page live in its
 * URL. `location.key` is undefined for the first entry in a session, so a pasted or
 * bookmarked link has nothing to pop and would otherwise leave the app.
 */
const BackButton = ({ history, hasHistory }) => (
  <IconButton
    size="small"
    aria-label="Back to users"
    onClick={() => (hasHistory ? history.goBack() : history.push('/users'))}
    sx={{ ml: -0.75, color: 'text.secondary' }}
  >
    <ChevronLeftIcon sx={{ fontSize: 22 }} />
  </IconButton>
);

/** Whole-account actions, which belong to no single tab. */
const AccountMenu = ({ canDelete, onDelete }) => {
  const [anchor, setAnchor] = useState(null);
  if (!canDelete) return null;

  return (
    <>
      <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)} aria-label="Account actions">
        <MoreVertIcon sx={{ fontSize: 19, color: 'text.secondary' }} />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        PaperProps={{ sx: { minWidth: 200 } }}
      >
        <MenuItem
          onClick={() => {
            setAnchor(null);
            onDelete();
          }}
          sx={{ fontSize: '13px', color: 'error.main' }}
        >
          Delete user
        </MenuItem>
      </Menu>
    </>
  );
};

const UserProfileShell = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const toast = useToast();
  const { id } = useParams();
  const user = useSelector((state) => state.users.selection.user);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Set by the Driver tab while a document is open, so the header and tabs get out of
  // the way. Reported up rather than read from the URL here as well, so there is one
  // source of truth for "the inspector has the page".
  const [inspecting, setInspecting] = useState(false);

  const canDeleteUser = useHasPermission('delete_users');
  // MANAGE_DRIVERS owns the document decisions and fleet changes on the Driver tab.
  const canManageDrivers = useHasPermission(PERMISSIONS.MANAGE_DRIVERS);
  // VIEW_DRIVER owns its read-only operating sections: history, money, reputation,
  // standing history and blocked dates.
  const canViewDrivers = useHasPermission(PERMISSIONS.VIEW_DRIVER);
  // CHANGE_DRIVER_STATUS earns the tab too, but only the approval half of it — see
  // DriverTab, which decides what an approver without MANAGE_DRIVERS may see. The
  // funnel's own page already lets them set application status; a person who can do
  // that from the grid but not from the person's page is the gap this closes.
  const canChangeDriverStatus = useHasPermission(PERMISSIONS.CHANGE_DRIVER_STATUS);

  // One instance per user, not one per render — an ad-hoc `new GrowthBook()` in the
  // body re-fetched features on every keystroke elsewhere on the page.
  const growthbook = useMemo(() => {
    const gb = new GrowthBook({
      apiHost: config.growthbook.apiHost,
      clientKey: config.growthbook.clientKey,
      subscribeToChanges: true,
    });
    gb.setAttributes({ user_id: id });
    return gb;
  }, [id]);

  useEffect(() => {
    fetchUserById(id)(dispatch);
  }, [id, dispatch]);

  const query = useQuery();

  const onDelete = async () => {
    setConfirmDelete(false);
    const { success, error } = await deleteUser(id)(dispatch);
    if (success) {
      toast.success('User deleted');
      history.push('/users');
    } else {
      // Surface the API's reason — the delete is refused for actionable causes (e.g.
      // they still have active rides or trips), not just failures.
      toast.error(error || 'Failed to delete user');
    }
  };

  if (!user) {
    return (
      <Stack alignItems="center" sx={{ pt: 10 }}>
        <LinearProgress sx={{ width: 300 }} />
      </Stack>
    );
  }

  // Parsed once. The raw document is the only place the Stripe connected-account id
  // lives, which is why it is threaded into both the strip and the Account tab.
  const userRawUnsafe = user.userRawUnsafe ? JSON.parse(user.userRawUnsafe.jsonString) : null;
  const name = `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unknown user';

  // A tab is only offered when the person has that half. A rider-only account showing
  // an empty Driver tab is the same mistake as the old page showing empty driver cards.
  const tabs = [
    { key: 'account', label: 'Account' },
    user.rider_id && { key: 'rider', label: 'Rider' },
    user.driver_id && (canManageDrivers || canViewDrivers || canChangeDriverStatus) && { key: 'driver', label: 'Driver' },
  ].filter(Boolean);

  // Validated against what is actually on offer, so a stale or hand-edited `?tab=driver`
  // on a rider — or from someone without the permission — falls back rather than
  // rendering nothing.
  const tab = tabs.some((t) => t.key === query.tab) ? query.tab : 'account';

  const setTab = (next) =>
    history.replace({ search: patchSearch(history.location.search, { tab: next === 'account' ? null : next }) });

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {/* Header and tabs both disappear while a document is open — see DriverTab. */}
      {!inspecting && (
        <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2.5, pb: 1.5, borderBottom: (t) => `1px solid ${hairline(t)}`, flexShrink: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
            <BackButton history={history} hasHistory={Boolean(history.location.key)} />
            {/* The uploader IS the avatar — it renders the image and owns the replace
                and photo-bypass flows, so there is no second copy of the picture. */}
            <ProfileImageUploader user={user} />
            {/* Truncates rather than `nowrap`. A `nowrap` name sat in a row that could
                not scroll, so on a phone a long one pushed the city and the account menu
                off the side of a card that clips — the menu was unreachable, not just
                ugly. An ellipsis keeps the row whole and the actions on screen. */}
            <Typography
              sx={{
                fontSize: { xs: '17px', sm: '20px' },
                fontWeight: 600,
                letterSpacing: '-0.01em',
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={name}
            >
              {name}
            </Typography>
            {/* No status pill beside the name. Driver standing lives entirely in the
                Driver tab's status section: this header is shared by every tab, and a
                driver-standing chip in it headlines a person who may be mostly a rider
                with their driver standing. */}
            <Box sx={{ flex: 1 }} />
            {/* Built by filtering, not by interpolating: `homeCity` is frequently a
                truthy object whose own fields are empty, so a template literal renders
                the literal string "undefined, undefined" — which it did, on the first
                real driver this was opened against.

                Hidden below sm rather than allowed to compete with the name for a
                phone's width: it is the least load-bearing thing in this row, and the
                Identity card carries the same city a scroll away. */}
            <Typography
              sx={{ fontSize: '12.5px', color: 'text.secondary', whiteSpace: 'nowrap', display: { xs: 'none', sm: 'block' } }}
            >
              {[user.homeCity?.cityName, user.homeCity?.stateAbbr].filter(Boolean).join(', ')}
            </Typography>
            <AccountMenu canDelete={canDeleteUser} onDelete={() => setConfirmDelete(true)} />
          </Stack>

          {/* `candidate_id` is read off the RAW user document. The authoritative copy is
              on the driver record (`api.drivers.getDriver` selects it), but that is only
              loaded once the Driver tab is opened, and a link that appears after you
              visit another tab is worse than one that is simply absent. ServiceStrip
              drops the entry when the id is missing, so the failure mode is "no Checkr
              link" rather than a broken one. ⚠️ Unverified against a real user document —
              confirm on staging, and if the field is not there, thread it up from
              DriverTab instead. */}
          {/* Rows 2 and 3 share ONE indent, set here rather than on each. The strip
              carried `ml: 5.5` and the tab bar carried nothing, so the tabs hung out
              past the back arrow while the strip lined up under the name — three header
              rows on three different left edges. 5.5 is the back button plus the row's
              own spacing, which is where the avatar and the name start. */}
          {/* The indent is dropped on a phone. Lining up under the name costs 44px of a
              375px screen, and the thing it aligns to — the avatar and the name — is
              directly above either way. */}
          <Box sx={{ ml: { xs: 0, sm: 5.5 } }}>
            <Box sx={{ mt: 1.25 }}>
              <ServiceStrip user={user} userRawUnsafe={userRawUnsafe} candidateId={userRawUnsafe?.candidate_id} />
            </Box>

            {/* Only worth a control when there is more than one thing to switch between.
                Full width on a phone, where three ~120px tabs would otherwise leave the
                control floating in a row it cannot fill. */}
            {tabs.length > 1 && (
              <Box sx={{ mt: 1.5 }}>
                <SegmentedControl
                  value={tab}
                  onChange={setTab}
                  options={tabs.map((t) => ({ key: t.key, label: t.label }))}
                  sx={{ maxWidth: { xs: '100%', sm: 120 * tabs.length } }}
                />
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* The Driver tab owns its own scrolling, because the inspector must be able to
          fix its document pane and scroll only its sidebar. Keeping it mounted at one
          position — rather than moving it in and out of a scroll wrapper — is what
          stops opening a document from refetching the whole record. */}
      {tab === 'driver' ? (
        <DriverTab user={user} userRawUnsafe={userRawUnsafe} name={name} onInspectingChange={setInspecting} />
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, ...scrollY }}>
          {/* 1240, not 880. Account and Rider are two-column grids now, and TabGrid
              stacks rather than squeezing below 420px a column — at 880 (832 inside
              the padding) it would have held one column and quietly thrown the layout
              away. 1240 gives two ~590px columns with the gap. */}
          <Box sx={{ maxWidth: 1240, mx: 'auto', px: { xs: 2, sm: 3 }, py: 2.5 }}>
            {tab === 'rider' ? (
              <RiderTab user={user} />
            ) : (
              <AccountTab user={user} userRawUnsafe={userRawUnsafe} growthbook={growthbook} />
            )}
          </Box>
        </Box>
      )}

      <ConfirmModal
        open={confirmDelete}
        title="Delete User"
        message={`Are you sure you want to delete ${name}? This action cannot be undone.`}
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={onDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </Box>
  );
};

/**
 * `flush`, because this page caps its own height so the inspector's document pane can
 * be fixed while only its sidebar scrolls — the same reason the standalone driver
 * profile does. A page that scrolls as a unit pushes the viewer's zoom and rotate
 * controls below the fold.
 */
const UserProfile = () => (
  <DashboardLayout flush title="User">
    <Box sx={flushPageSx}>
      <Paper elevation={0} sx={{ ...surfaceCardSx, flex: 1, minWidth: 0 }}>
        <UserProfileShell />
      </Paper>
    </Box>
  </DashboardLayout>
);

export default UserProfile;
